// @ts-strict-ignore
import type { QueryResult } from "@apollo/client";
import { Fragment, useEffect, type RefObject } from "react";
import { useTranslation } from "react-i18next";

import { Separator, Typography } from "@aviary";
import { useDropdownContext } from "@aviary/components/Dropdown/DropdownContext";
import { emersonRoutes } from "@clinic/emerson/data/emersonRoutes";
import type {
  MegaMenuData,
  Variables,
} from "@clinic/shared/data/queries/CatalogMegaMenuQuery/CatalogMegaMenu.query";
import { CategoryLink } from "@emerson/components/CategoryLink/CategoryLink";
import { ShopSpecials } from "@emerson/components/ShopSpecials/ShopSpecials";
import type { WordpressSpecialType } from "@emerson/data/queries/WordpressSpecialsQuery/WordpressSpecialsQuery.query";
import { l } from "@emerson/locales/i18n";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import type { MenuCategory } from "@shared/types/graphqlGenerated";

import { MenuItem } from "../MenuItem/MenuItem";
import { SideMenu } from "../SideMenu/SideMenu";

import { SkeletonShopDropdown } from "./SkeletonShopDropdown";

import * as styles from "./ShopMenu.styles";

type Props = {
  queryResult: QueryResult<MegaMenuData, Variables>;
  showCategory: MenuCategory;
  isSideMenuOpen: boolean;
  showSpecialContent: boolean;
  sideMenuCallbacks: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onFocus: () => void;
    onBlur: () => void;
  };
  triggerCallbacks: {
    onMouseEnter: (event: React.MouseEvent, menuCategory: MenuCategory) => void;
    onMouseLeave: () => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
    onFocus: (menuCategory: MenuCategory) => void;
    onBlur: () => void;
    onClick: (event: React.MouseEvent) => void;
  };
  specialMenuItemCallbacks: {
    onMouseEnter: (e: React.SyntheticEvent) => void;
    onFocus: () => void;
  };
  specialsData: {
    specials: WordpressSpecialType[];
    showSpecials: boolean;
  };
  ref?: RefObject<HTMLDivElement>;
};

const ShopMenu = ({
  ref,
  queryResult: { data, loading },
  showCategory,
  isSideMenuOpen,
  showSpecialContent,
  triggerCallbacks,
  sideMenuCallbacks,
  specialMenuItemCallbacks,
  specialsData: { specials, showSpecials },
}: Props) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalyticsLogger();
  const { mouseOverContent } = useDropdownContext();
  const { isUnauthenticated } = useSharedGlobalConfig();
  const { menuCategories } = data?.viewer || {};

  const onClickHandler = e => {
    triggerCallbacks.onClick(e);
  };

  const onMenuTrigger = () => {
    logAnalyticsEvent({
      eventName: isUnauthenticated
        ? AnalyticsEvent.EMERSON_UNAUTH_SHOP_MENU_DROPDOWN
        : AnalyticsEvent.EMERSON_SHOP_MENU_DROPDOWN,
      payload: {
        origin_url: location.pathname,
        automatic_event: false,
      },
    });
  };

  useEffect(() => {
    if (mouseOverContent) onMenuTrigger();
  }, [mouseOverContent]);

  const renderSubCategories = (menuCategory: MenuCategory) => {
    if (menuCategory.subCategories.length <= 1) {
      return null;
    }

    return (
      <SideMenu
        isSideMenuOpen={!!isSideMenuOpen && showCategory?.name === menuCategory.name}
        onMouseEnter={sideMenuCallbacks.onMouseEnter}
        onMouseLeave={sideMenuCallbacks.onMouseLeave}
      >
        <div>
          <CategoryLink
            category={showCategory}
            categoryName={showCategory?.name}
            linkStyles={styles.heading}
            isHoverUnderLined
            sideMenuCallbacks={sideMenuCallbacks}
            role="menuitem"
            isSecondaryWeight
          />
          <ul css={styles.links} role="none">
            {showCategory?.subCategories?.map(subCategory => (
              <li key={subCategory.name} role="none">
                <MenuItem
                  hasSubMenu={false}
                  menuCategory={subCategory}
                  parentCategoryName={showCategory.name}
                  onFocus={sideMenuCallbacks.onFocus}
                  onBlur={sideMenuCallbacks.onBlur}
                  isSideMenuOpen={isSideMenuOpen}
                />
              </li>
            ))}
          </ul>
        </div>
      </SideMenu>
    );
  };

  const renderSpecialContent = () => {
    if (!showSpecials) return null;

    return (
      <SideMenu
        isSideMenuOpen={!!isSideMenuOpen && showSpecialContent}
        onMouseEnter={sideMenuCallbacks.onMouseEnter}
        onMouseLeave={sideMenuCallbacks.onMouseLeave}
      >
        <Typography isSecondaryWeight css={styles.heading} role="menuitem">
          {t(l.header.Shop.Specials)}
        </Typography>

        <ShopSpecials sideMenuCallbacks={sideMenuCallbacks} specials={specials} />
      </SideMenu>
    );
  };

  return (
    <div css={styles.shopMenu} role="menu">
      {!!loading && <SkeletonShopDropdown />}
      {menuCategories?.map(menuCategory => (
        <Fragment key={menuCategory.name}>
          <MenuItem
            ref={ref}
            hasSubMenu={menuCategory.subCategories.length > 1}
            isMainCategory
            menuCategory={menuCategory}
            isActive={showCategory?.name === menuCategory.name}
            isSideMenuOpen={isSideMenuOpen}
            {...triggerCallbacks}
            onClick={onClickHandler}
          />
          {renderSubCategories(menuCategory)}
        </Fragment>
      ))}

      <Separator />
      {!isUnauthenticated && (
        <MenuItem
          ref={ref}
          text={t(l.header.Shop.ReorderProducts)}
          hasSubMenu={false}
          isMainCategory
          isSideMenuOpen={false}
          {...triggerCallbacks}
          onClick={onClickHandler}
          to={emersonRoutes.orders.reorderProducts}
        />
      )}
      <MenuItem
        ref={ref}
        text={t(l.header.Shop.New)}
        hasSubMenu={false}
        isMainCategory
        isSideMenuOpen={false}
        {...triggerCallbacks}
        onClick={onClickHandler}
        to={emersonRoutes.shop.isNew}
      />
      <MenuItem
        ref={ref}
        text={t(l.header.Shop.Specials)}
        hasSubMenu={showSpecials}
        isMainCategory
        isActive={showSpecialContent}
        isSideMenuOpen={isSideMenuOpen}
        {...triggerCallbacks}
        {...specialMenuItemCallbacks}
        onClick={onClickHandler}
        to={emersonRoutes.external.specials}
        isExternalLink
      />
      {renderSpecialContent()}
    </div>
  );
};

export { ShopMenu };
